/**
 * Sidebar width bootstrap (issue #1604).
 *
 * Mirrors the localStorage-mirror pattern used by `themeBootstrap.ts` (#424):
 * the resizable sidebar's width is mirrored to localStorage on every resize
 * so it survives a reload, and the mirror is read back into `uiSlice`'s
 * initial state. There is no server-side counterpart (unlike theme/locale/
 * timezone) — sidebar width is a pure client-side layout preference.
 */

/** localStorage key for the persisted sidebar width. */
export const SIDEBAR_WIDTH_STORAGE_KEY = 'sidebarWidth';

/** Default width (px) — matches the pre-#1604 hardcoded `Sidebar.css` value. */
export const SIDEBAR_DEFAULT_WIDTH = 280;

/** Minimum drag-resize width (px) — narrow enough to still fit icons + short labels. */
export const SIDEBAR_MIN_WIDTH = 200;

/** Maximum drag-resize width (px) — wide enough for the longest nav label without dominating the layout. */
export const SIDEBAR_MAX_WIDTH = 480;

/** Clamp an arbitrary width to the supported drag-resize range. */
export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
}

/** Read the persisted sidebar width from localStorage, defaulting when absent/invalid. */
export function readStoredSidebarWidth(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (raw === null) return SIDEBAR_DEFAULT_WIDTH;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return SIDEBAR_DEFAULT_WIDTH;
    return clampSidebarWidth(parsed);
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

/** Mirror a (already-clamped) sidebar width to localStorage. */
export function writeStoredSidebarWidth(width: number): void {
  try {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(width));
  } catch {
    // localStorage may be unavailable (private mode / SSR); width still applied in-memory.
  }
}
